import type { WebgpuAdapterProfile, WebgpuSignals } from '@fraudeai/schema';
import { defineModule } from '../harness.js';
import { hashValue } from '../util/hash.js';
import { attemptAsync } from '../util/safe.js';

/**
 * WebGPU-адаптер.
 *
 * Полезен по двум причинам. Во-первых, `adapter.info` отдаёт вендора и
 * архитектуру GPU напрямую, без обходного пути через WEBGL_debug_renderer_info —
 * и антидетекты часто патчат только второй. Расхождение между ними —
 * готовое срабатывание. Во-вторых, `isFallbackAdapter` честно сообщает,
 * что железа нет.
 */

interface GpuAdapterInfo {
  vendor?: string;
  architecture?: string;
  device?: string;
  description?: string;
}

interface GpuAdapter {
  info?: GpuAdapterInfo;
  requestAdapterInfo?: () => Promise<GpuAdapterInfo>;
  features?: Iterable<string>;
  limits?: Record<string, number>;
  isFallbackAdapter?: boolean;
}

interface Gpu {
  requestAdapter: (options?: { powerPreference?: string }) => Promise<GpuAdapter | null>;
}

type PowerPreference = WebgpuAdapterProfile['powerPreference'];

const PROFILE_VERSION = 1;

const EMPTY: WebgpuSignals = {
  supported: false,
  vendor: null,
  architecture: null,
  device: null,
  description: null,
  features: null,
  limits: null,
  profileVersion: null,
  profileHash: null,
  profiles: null,
  isFallbackAdapter: null,
};

function normalizeLimits(limits: GpuAdapter['limits']): Record<string, number> | null {
  if (!limits) return null;
  const out: Record<string, number> = {};
  for (const key in limits) {
    const value = limits[key];
    if (typeof value === 'number' && Number.isFinite(value)) out[key] = value;
  }
  return out;
}

async function readInfo(adapter: GpuAdapter): Promise<GpuAdapterInfo | null> {
  return adapter.info ?? (await attemptAsync(() => adapter.requestAdapterInfo?.())) ?? null;
}

async function readAdapterProfile(
  gpu: Gpu,
  powerPreference: PowerPreference,
): Promise<{
  adapter: GpuAdapter | null;
  info: GpuAdapterInfo | null;
  features: string[] | null;
  limits: Record<string, number> | null;
  profile: WebgpuAdapterProfile | null;
}> {
  const adapter = await attemptAsync(() => gpu.requestAdapter({ powerPreference }));
  if (!adapter) {
    return { adapter: null, info: null, features: null, limits: null, profile: null };
  }

  const info = await readInfo(adapter);
  const features = await attemptAsync(() =>
    adapter.features ? Array.from(adapter.features, String).sort() : null,
  );
  const limits = await attemptAsync(() => normalizeLimits(adapter.limits));
  const isFallbackAdapter =
    typeof adapter.isFallbackAdapter === 'boolean' ? adapter.isFallbackAdapter : null;

  const body = {
    powerPreference,
    vendor: info?.vendor ?? null,
    architecture: info?.architecture ?? null,
    device: info?.device ?? null,
    description: info?.description ?? null,
    features,
    limits,
    isFallbackAdapter,
  };

  const profile: WebgpuAdapterProfile = {
    powerPreference,
    vendor: body.vendor,
    architecture: body.architecture,
    device: body.device,
    description: body.description,
    featureCount: features ? features.length : null,
    limitCount: limits ? Object.keys(limits).length : null,
    featuresHash: features ? hashValue(features) : null,
    limitsHash: limits ? hashValue(limits) : null,
    isFallbackAdapter,
    hash: hashValue({ version: PROFILE_VERSION, ...body }),
  };

  return { adapter, info, features, limits, profile };
}

export const webgpuModule = defineModule({
  id: 'webgpu',
  phase: 1,
  timeoutMs: 5000,
  async run(): Promise<WebgpuSignals> {
    const gpu = (navigator as Navigator & { gpu?: Gpu }).gpu;
    if (!gpu?.requestAdapter) return EMPTY;

    const high = await readAdapterProfile(gpu, 'high-performance');
    const low = await readAdapterProfile(gpu, 'low-power');
    const primary = high.adapter ? high : low;
    const profiles = [high.profile, low.profile].filter((p): p is WebgpuAdapterProfile => Boolean(p));

    return {
      supported: true,
      vendor: primary.info?.vendor ?? null,
      architecture: primary.info?.architecture ?? null,
      device: primary.info?.device ?? null,
      description: primary.info?.description ?? null,
      features: primary.features,
      limits: primary.limits,
      profileVersion: PROFILE_VERSION,
      profileHash: profiles.length
        ? hashValue({
            version: PROFILE_VERSION,
            adapters: profiles.map((p) => ({
              powerPreference: p.powerPreference,
              hash: p.hash,
            })),
          })
        : null,
      profiles,
      isFallbackAdapter: primary.profile?.isFallbackAdapter ?? null,
    };
  },
});
